import { BCMSCurrency, BCMSDate, BCMSDefaultModel } from "@bcm-dev/bcms-vite";
import { Account, Bill, Payment } from "model";

/**
 * @class MultiPayItem
 * @classdesc The class definition for the MultiPayItem class
 * 
 * @param {Bill} bill - The bill that should be paid
 * 
 * @property {Bill} _bill - The bill that the payment is for
 * @property {BCMSCurrency} _amount - The amount of the payment
 * @property {Account} _account - The account being paid from
 * @property {BCMSDate} _date - The date of the payment
 * @property {STRING} _memo - The memo of the payment
 */
export default class MultiPayItem {
    _bill;
    _amount;
    _account;
    _date;
    _memo;
    _hasFee;
    _feeAmount;
    _withdrawnOnDate;
    
    constructor(bill) {
        this.bill = bill;
        this.amount = BCMSCurrency.init(0);
        this.account = Account.firstInstanceOf();
        this.hasFee = false;
        this.feeAmount = BCMSCurrency.init(0);
    }

    /**
     * @function bill
     * @instance
     * @param {STRING|Bill} value - The value to be set for the bill property
     * @description Setter for the bill property
     * @memberof MultiPayItem
     */
    set bill(value) {
        this.checkValue(value, Function, "Error setting the bill property of a MultiPayItem. Either pass an id or a Bill instance");
        this._bill = value instanceof Function ? value : Bill.initByID(value);

        // Initalize date based on the bill and biller
        const date = BCMSDate.init();

        if(this._bill.biller.paymentType === "paper") {
            Array.apply(null, Array(5)).forEach(i => date.nextBusinessDay());
        }
        else { date.nextBusinessDay(); }

        this._date = date;
    }

     /**
      * @function amount
      * @instance
      * @param {STRING|BCMSCurrency} - The value to be set for the amount property
      * @description Setter for the amount property
      * @memberof MultiPayItem
      */
    set amount(value) {
        this.checkValue(value, BCMSCurrency, "Error setting the amount property of a MultiPayItem. Either pass a valid amount or a BCMSCurrency instance");
        this._amount = value instanceof BCMSCurrency ? value : BCMSCurrency.init(value.length !== 0 ? value : 0);
    }

    /**
     * @function account
     * @instance
     * @param {STRING|Account} value - The value to be set for the account property
     * @description Setter for the account property
     * @memberof MultiPayItem
     */
    set account(value) {
        this.checkValue(value, Account, "Error setting the account property of a MultiPayItem. Either pass an id or an Account instance");
        this._account = value instanceof Account ? value : Account.initByID(value);
    }

    /**
     * @function date
     * @instance
     * @param {STRING|BCMSDate} value - The value to be set for the date property
     * @description Setter for the date property
     * @memberof MultiPayItem
     */
    set date(value) {
        this.checkValue(value, BCMSDate, "Error setting the date property of a MultiPayItem. Either pass an offset or a BCMSDate instance");
        this._date = value instanceof BCMSDate ? value : BCMSDate.init().offset(value);
    }

    /**
     * @function memo
     * @instance
     * @param {STRING} value - The value to be set for the memo property
     * @description Setter for the memo property
     * @memberof MultiPayItem
     */
    set memo(value) { this._memo = value; }

    /**
     * @function hasFee
     * @instance
     * @param {STRING} value - The value to be set for the hasFee property
     * @description Setter for the hasFee property
     * @memberof MultiPayItem
     */
    set hasFee(value) { this._hasFee = value; }

    /**
     * @function feeAmount
     * @instance
     * @param {STRING|BCMSCurrency} - The value to be set for the feeAmount property
     * @description Setter for the feeAmount property
     * @memberof MultiPayItem
     */
    set feeAmount(value) {
        this.checkValue(value, BCMSCurrency, "Error setting the amount property of a MultiPayItem. Either pass a valid amount or a BCMSCurrency instance");
        this._feeAmount = value instanceof BCMSCurrency ? value : BCMSCurrency.init(value.length !== 0 ? value : 0);
    }

    /**
     * @function withdrawnOnDate
     * @instance
     * @param {STRING|BCMSDate} value - The value to be set for the withdrawnOnDate property
     * @description Setter for the withdrawnOnDate property
     * @memberof MultiPayItem
     */
    set withdrawnOnDate(value) {
        this.checkValue(value, BCMSDate, "Error setting the date property of a MultiPayItem. Either pass an offset or a BCMSDate instance");
        this._withdrawnOnDate = value instanceof BCMSDate ? value : BCMSDate.init().offset(value);
    }

    /**
     * @function bill
     * @instance
     * @returns {Bill}
     * @description Getter for the bill property
     * @memberof MultiPayItem
     */
    get bill() { return this._bill; }

    /**
     * @function amount
     * @instance
     * @returns {BCMSCurrency}
     * @description Getter for the amount property
     * @memberof MultiPayItem
     */
    get amount() { return this._amount; }

    /**
     * @function account
     * @instance
     * @returns {Account}
     * @description Getter for the account property
     * @memberof MultiPayItem
     */
    get account() { return this._account; }

    /**
     * @function date
     * @instance
     * @returns {BCMSDate}
     * @description Getter for the date property
     * @memberof MultiPayItem
     */
    get date() { return this._date; }

    /**
     * @function memo
     * @instance
     * @returns {STRING}
     * @description Getter for the memo property
     * @memberof MultiPayItem
     */
    get memo() { return this._memo; }

    /**
     * @function feeAmount
     * @instance
     * @returns {Account}
     * @description Getter for the feeAmount property
     * @memberof MultiPayItem
     */
    get feeAmount() { return this._feeAmount; }

    /**
     * @function payment
     * @instance
     * @returns {Payment}
     * @description Getter for creating a new payment for the bill and values set
     * @memberof MultiPayItem
     */
    get payment() {
        const defaultPayment = BCMSDefaultModel.payment();
        const payment = Payment.init({});

        payment.id = Payment.nextID();
        payment.from = this.account;
        payment.to = this.bill.biller;
        payment.amount = this.amount;
        payment.date = this.date;

        const merged = BCMSDefaultModel.mergeRecords(payment, defaultPayment);
        const newPayment = Payment.init(merged);

        this.amount = newPayment.amount;
        return newPayment;
    }

    /**
     * @function withdrawnOnDate
     * @instance
     * @returns {BCMSDate}
     * @description Getter for the withdrawnOnDate property
     * @memberof MultiPayItem
     */
    get withdrawnOnDate() { return this._withdrawnOnDate; }

    /**
     * @function checkValue
     * @instance
     * @param {*} value - The value that needs to be tested
     * @param {*} instance - The type of instance to test against
     * @param {STRING} message - The error message that should be shown if the check fails
     * @description Function to test a value and throw an error if it fails
     * @memberof MultiPayItem
     */
    checkValue(value, instance, message) {
        if(!(value instanceof instance) && isNaN(value)) {
            throw new Error(message);
        }
    }

    /**
     * @function formatDate
     * @instance
     * @returns {STRING}
     * @description Formats the set date based on the year of the date
     * @memberof MultiPayItem
     */
    formatDate() {
        const currentYear = BCMSDate.init().yearInt;
        
        return currentYear !== this.date.yearInt
            ? this.date.format("M d, Y")
            : this.date.format("M d");
    }

    /**
     * @function init
     * @static
     * @param {STRING|Bill} bill - The value to be set for the bill
     * @returns {MultiPayItem}
     * @description Static init method to create a new MultiPayItem instance
     * @memberof MultiPayItem
     */
    static init(bill) {
        return new MultiPayItem(bill);
    }
}